import { useId, useState } from 'react'
import { PaperAirplaneIcon, PaperClipIcon } from '@heroicons/react/24/outline'
import {
  AttachmentPicker,
  readAttachments,
} from '../../../app/account/support/AttachmentPicker'
import { escapeHtml, supportField } from '../../../app/account/support/supportTypes'
import type { ChatMessageInput } from '@/lib/support-chat/types'

export function ChatComposer({
  starting,
  signedIn,
  disabled,
  initial,
  onTyping,
  onSend,
}: {
  starting: boolean
  signedIn: boolean
  disabled: boolean
  initial?: ChatMessageInput
  onTyping: () => void
  onSend: (input: ChatMessageInput) => Promise<boolean>
}) {
  const messageId = useId()
  const [name, setName] = useState(initial?.name ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [text, setText] = useState(() =>
    initial
      ? (new DOMParser().parseFromString(
          initial.html.replace(/<br\s*\/?\s*>/gi, '\n'),
          'text/html'
        ).body.textContent ?? '')
      : ''
  )
  const [files, setFiles] = useState<File[]>(() =>
    (initial?.attachments ?? []).map(
      file =>
        new File(
          [
            Uint8Array.from(atob(file.base64), character =>
              character.charCodeAt(0)
            ),
          ],
          file.name,
          { type: file.contentType }
        )
    )
  )
  const [error, setError] = useState<string | null>(null)
  const [preparing, setPreparing] = useState(false)
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(
    Boolean(initial?.attachments?.length)
  )
  return (
    <form
      className="space-y-2"
      onSubmit={async event => {
        event.preventDefault()
        if (preparing || disabled) return
        setPreparing(true)
        setError(null)
        try {
          const input: ChatMessageInput = {
            requestId: crypto.randomUUID(),
            html: `<p>${escapeHtml(text).replace(/\n/g, '<br>')}</p>`,
            attachments: await readAttachments(files),
            ...(starting
              ? {
                  name: signedIn ? 'Account customer' : name,
                  email: email.trim() || null,
                  orderId: null,
                  productId: null,
                }
              : {}),
          }
          if (await onSend(input)) {
            setText('')
            setFiles([])
            setAttachmentsExpanded(false)
          }
        } catch (error) {
          setError(
            error instanceof Error
              ? error.message
              : 'Your message could not be prepared.'
          )
        } finally {
          setPreparing(false)
        }
      }}
    >
      <fieldset disabled={disabled || preparing} className="space-y-2">
        {starting && !signedIn && (
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block text-xs text-gray-500">
              Your name
              <input
                className={supportField}
                value={name}
                onChange={event => setName(event.target.value)}
                autoComplete="name"
                maxLength={200}
                required
              />
            </label>
            <label className="block text-xs text-gray-500">
              Email (optional)
              <input
                className={supportField}
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                autoComplete="email"
                maxLength={320}
              />
            </label>
          </div>
        )}
        <div className="flex items-end gap-1 rounded-3xl border border-gray-200 bg-white px-2 py-1.5 shadow-sm focus-within:border-vi-red">
          <button
            type="button"
            className="mb-0.5 flex size-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-950"
            onClick={() => setAttachmentsExpanded(open => !open)}
          >
            <PaperClipIcon className="size-5" aria-hidden="true" />
            <span className="sr-only">Attach files</span>
          </button>
          <label htmlFor={messageId} className="sr-only">
            {starting ? 'How can we help?' : 'Your message'}
          </label>
          <textarea
            id={messageId}
            className="max-h-32 min-h-9 flex-1 resize-none border-0 bg-transparent px-1 py-2 text-sm leading-5 text-gray-950 outline-none placeholder:text-gray-400"
            required
            rows={1}
            maxLength={20000}
            placeholder={starting ? 'How can we help?' : 'Ask a question…'}
            value={text}
            onChange={event => {
              setText(event.target.value)
              onTyping()
            }}
          />
          <button
            className="mb-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-vi-red text-white hover:bg-red-800 disabled:bg-gray-200 disabled:text-gray-400"
            disabled={disabled || preparing || !text.trim()}
          >
            <PaperAirplaneIcon className="size-4" aria-hidden="true" />
            <span className="sr-only">
              {preparing ? 'Sending…' : 'Send message'}
            </span>
          </button>
        </div>
        {attachmentsExpanded && (
          <AttachmentPicker
            files={files}
            onChange={setFiles}
            error={error}
            onError={setError}
          />
        )}
      </fieldset>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </form>
  )
}
